// ── Daily World Quest generator (Feb 2026 · Cycle 64.5 rework) ─────────
// Every day we assemble ONE 5-stop quest anchored to a country and, per
// user direction (Feb 2026), a VARIETY of learning products:
//   Treasure Quest · Maths Academy · Maths Quest · Forest Quest · Quiz
// Each stop still shares the day's country theme so the whole quest
// reads as one story — but now the child rotates through named
// learning surfaces rather than 5 generic "explore/discover/solve"
// stops. This makes the daily quest a proper tour of the app's five
// flagship products.
//
// Determinism: the country + rotation offsets are hashed from the ISO
// calendar date so every device sees the same quest on the same day.
// Tomorrow's teaser (via /components/TomorrowHook) reads by generating
// tomorrow's date and pulling the country.

import { COUNTRIES } from "@/data/countries";
import { factForCountry } from "@/data/countryFacts";
import { slugify } from "@/lib/countrySlug";
import { getBirthCountryCode, isFirstVisitDay } from "@/lib/kidProfile";

/** Stable 32-bit hash for a short string. */
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** ISO date `YYYY-MM-DD` for a given Date, in the device's LOCAL
 *  timezone. Local (not UTC) so the quest key rotates at the child's
 *  own midnight and stays consistent with the local day-of-year
 *  country rotation below (Jun 2026 cleanup · timezone fix). */
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Day-of-year integer for a Date (matches Home's Country of the Day). */
function dayOfYearFor(d) {
  return Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86400000);
}

/**
 * Canonical "Country of the Day" resolver — the SINGLE source of truth
 * shared by the Home Country-of-the-Day card and the World Quest
 * (DailyWorldQuest) so both always feature the same country.
 *
 * Rules (mirrors the previous Home logic):
 *   • deterministic day-of-year rotation through COUNTRIES
 *   • on the child's very first visit day, their birth country (if set)
 *     becomes today's country — applied to TODAY only, never to a
 *     future/teaser date.
 */
export function countryOfDay(date = new Date()) {
  const day = dayOfYearFor(date);
  const isToday = day === dayOfYearFor(new Date());
  if (isToday) {
    const birthCode = getBirthCountryCode();
    if (birthCode && isFirstVisitDay()) {
      const bc = COUNTRIES.find((c) => c.code === birthCode);
      if (bc) return bc;
    }
  }
  return COUNTRIES[day % COUNTRIES.length];
}

// ── Product library ────────────────────────────────────────────────
// Each entry is one flagship learning surface. The stop generator picks
// N of these per day (5 for explorers/pioneers, 4 for seedlings) using
// a date-hashed rotation so the surface mix varies from day to day.
// The `to` builder receives the country of the day so the destination
// URL carries the country context wherever the child lands.
const PRODUCT_LIBRARY = [
  {
    id: "treasure",
    name: "Treasure Quest",
    emoji: "💎",
    tierLabel: {
      seedlings: "Treasure hunt",
      explorers: "Hunt today's treasure",
      pioneers:  "Treasure Quest — themed run",
    },
    hint: (country) => `Solve today's Treasure Quest with a ${country.name} twist.`,
    to:   (country) => `/treasure-quest?theme=${encodeURIComponent(country.name)}`,
    minutes: 5,
    tags: ["adventure", "puzzles"],
  },
  {
    id: "maths-academy",
    name: "Maths Academy",
    emoji: "🎓",
    tierLabel: {
      seedlings: "Numbers today",
      explorers: "Maths Academy skill",
      pioneers:  "Maths Academy — one skill",
    },
    hint: (country) => `Practise today's Maths Academy skill — one short unit.`,
    // Library route is safe for every tier + all years.
    to:   () => `/academy/maths/library`,
    minutes: 6,
    tags: ["maths"],
  },
  {
    id: "maths-quest",
    name: "Maths Quest",
    emoji: "🧮",
    tierLabel: {
      seedlings: "3 tiny puzzles",
      explorers: "Solve 3 maths puzzles",
      pioneers:  "Maths Quest — themed problems",
    },
    hint: (country) => `Three quick maths puzzles set in ${country.name}.`,
    to:   (country) => `/math?theme=${encodeURIComponent(country.name)}`,
    minutes: 5,
    tags: ["maths", "quiz"],
  },
  {
    id: "forest-quest",
    name: "Forest Quest",
    emoji: "🌳",
    tierLabel: {
      seedlings: "Meet an animal",
      explorers: "Forest Quest — nature find",
      pioneers:  "Forest Quest — ecosystem tour",
    },
    hint: (country) => `Explore an animal or habitat unique to ${country.name}.`,
    to:   (country) => `/explore/nature?country=${country.code}`,
    minutes: 4,
    tags: ["nature", "science"],
  },
  {
    id: "quiz",
    name: "Daily Quiz",
    emoji: "🧠",
    tierLabel: {
      seedlings: "Quick quiz",
      explorers: "Take today's quiz",
      pioneers:  "Themed knowledge quiz",
    },
    hint: (country) => `A short quiz — questions about ${country.name}.`,
    to:   (country) => `/quiz?theme=${encodeURIComponent(country.name)}`,
    minutes: 4,
    tags: ["quiz"],
  },
  // Feb 2026 · Cycle 64.8 — Language Quest as a daily stop. Reuses the
  // same country context so kids meet words connected to the day's
  // country (e.g. Mali → jungle, monsoon → India). Full 8-game module
  // arrives in later cycles; v1 ships Word Builder + Meaning Match +
  // Missing Letter which all work here.
  {
    id: "language",
    name: "Language Quest",
    emoji: "🔤",
    tierLabel: {
      seedlings: "Build 3 words",
      explorers: "Word games — build, match, spell",
      pioneers:  "Language Quest — word origins",
    },
    hint: () => `Three quick word games — build, match a meaning, fill the blank.`,
    to:   () => `/language-quest?fromQuest=1`,
    minutes: 4,
    tags: ["english", "vocabulary"],
  },
  {
    id: "explore",
    name: "Globe Explore",
    emoji: "🌍",
    tierLabel: {
      seedlings: "Meet today's country",
      explorers: "Find & meet the country",
      pioneers:  "Locate the country + facts",
    },
    hint: (country) => `Zoom to ${country.name} and read the facts.`,
    // Feb 2026 · Cycle 64.7 — link to the country detail page (facts,
    // flag, animal, landmark, quick round) instead of the raw globe
    // so tapping the stop actually shows the child what the country
    // IS, not just where it is on the sphere.
    to:   (country) => `/country/${slugify(country.name)}?fromQuest=1`,
    minutes: 3,
    tags: ["geography"],
  },
];

// Every stop must at least appear in the pool — user asked for the five
// named products FIRST, then Globe Explore as the anchor if we're short
// on slots. Order defines the default day-0 rotation; the date hash
// rotates the same list so the mix shifts across the week.
const PRIMARY_IDS = ["treasure", "maths-academy", "maths-quest", "forest-quest", "quiz", "language"];
const SUPPORT_IDS = ["explore"]; // globe anchor for seedlings (4-stop path)

function pickForDate(dateIso, count) {
  const total = PRIMARY_IDS.length + SUPPORT_IDS.length;
  const offset = hashString(dateIso + "-mix") % total;
  const ordered = [...PRIMARY_IDS, ...SUPPORT_IDS];
  const rotated = [...ordered.slice(offset), ...ordered.slice(0, offset)];
  // Always keep the globe anchor for seedlings so day 1 still feels
  // grounded in geography. Pioneers/explorers get the full 5-product
  // rotation.
  if (count === 4) {
    const withAnchor = rotated.filter((id) => id !== "explore");
    return ["explore", ...withAnchor].slice(0, 4);
  }
  return rotated.slice(0, count);
}

function productById(id) {
  return PRODUCT_LIBRARY.find((p) => p.id === id) || null;
}

function labelFor(product, tier) {
  const map = product.tierLabel || {};
  return map[tier] || map.explorers || product.name;
}

// ── Stop builder ────────────────────────────────────────────────────
function buildStops(country, adaptiveCount, tier, dateIso) {
  const tierKey = tier === "seedlings" || tier === "pioneers" ? tier : "explorers";
  const ids = pickForDate(dateIso, adaptiveCount);
  const stops = ids.map((id, i) => {
    const p = productById(id);
    if (!p) return null;
    const base = p.to(country);
    const sep = base.includes("?") ? "&" : "?";
    const to = `${base}${sep}stop=${i + 1}&of=${ids.length}&fromQuest=1`;
    return {
      id: p.id,
      label: labelFor(p, tierKey),
      hint: p.hint(country),
      to,
      emoji: p.emoji,
      minutes: p.minutes,
      productName: p.name,
    };
  }).filter(Boolean);
  return stops;
}

/** Pick the adaptive stop count from the current tier. Seedlings get 4
 *  (globe anchor + 3 rotating products); explorers/pioneers get the
 *  full 5-product tour. */
function stopsFor(tier) {
  if (tier === "seedlings") return 4;
  return 5;
}

/** Build the daily quest object for the given date and tier. */
export function generateDailyQuest({ tier = null, date = new Date() } = {}) {
  const iso = isoDate(date);
  const country = countryOfDay(date);
  const count = stopsFor(tier);
  const stops = buildStops(country, count, tier, iso);
  const totalMinutes = stops.reduce((n, s) => n + (s.minutes || 4), 0);
  // Tier-tuned intro copy — seedlings get a warm cheer, pioneers get a
  // fuller factual paragraph naming the flagship products, explorers
  // get the balanced one-liner.
  const productChain = stops.map((s) => s.productName).slice(0, count).join(" · ");
  const intro = tier === "seedlings"
    ? `🌍 Today we visit ${country.name}!`
    : tier === "pioneers"
      ? `Today's quest lands in ${country.name}. Five short stops across ${productChain} — all anchored around one country and one story.`
      : `Today's quest lands in ${country.name}. Five stops across ${productChain}.`;
  return {
    date: iso,
    country: { code: country.code, name: country.name, emoji: country.emoji || "🌍" },
    stops,
    totalMinutes,
    intro,
    tier,
  };
}

/** Convenience: today's quest for the given tier. */
export function todayQuest(tier) {
  return generateDailyQuest({ tier });
}

/** Convenience: tomorrow's quest for the given tier — used by the
 *  Tomorrow-hook to name a concrete return reason. */
export function tomorrowQuest(tier) {
  const t = new Date(Date.now() + 86_400_000);
  return generateDailyQuest({ tier, date: t });
}
